import { motion } from 'framer-motion';
import { Layers, Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Exploded View Animation — extracted from DesignStudio.jsx (Feb 2026 refactor)
export default function ExplodedViewAnimation({ explodedData, isPlaying, onToggle, language }) {
  if (!explodedData) return null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-slate-900/80 border border-purple-500/30 rounded-xl p-4 space-y-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-purple-400" />
          <span className="font-semibold text-white">
            {language === 'tr' ? 'Montaj Sırası Animasyonu' : 'Assembly Sequence Animation'}
          </span>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onToggle}
          className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
        >
          {isPlaying ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
          {isPlaying ? (language === 'tr' ? 'Durdur' : 'Pause') : (language === 'tr' ? 'Oynat' : 'Play')}
        </Button>
      </div>

      <div
        data-testid="assembly-animation-disclaimer"
        className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs leading-relaxed text-amber-200"
      >
        {language === 'tr'
          ? 'Şematik montaj animasyonu — doğrulanmış kinematik, FEA veya üretim simülasyonu değildir.'
          : 'Schematic assembly animation — not a validated kinematic, FEA, or manufacturing simulation.'}
      </div>

      <div className="relative">
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
          <motion.div
            animate={isPlaying ? { width: ['0%', '100%'] } : {}}
            transition={{ duration: explodedData.total_duration || 2, repeat: Infinity, ease: 'linear' }}
            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
        {explodedData.components?.slice(0, 8).map((comp, i) => (
          <motion.div
            key={i}
            initial={{ x: 0 }}
            animate={isPlaying ? {
              x: [0, (comp.exploded_position?.[0] - comp.original_position?.[0]) * 2 || 20, 0],
            } : {}}
            transition={{
              duration: explodedData.total_duration || 2,
              delay: comp.animation_delay || i * 0.1,
              repeat: Infinity,
            }}
            className="bg-slate-800/50 rounded-lg p-2 border border-slate-700"
          >
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: comp.color || '#6366F1' }} />
              <span className="text-slate-300 text-xs truncate">{comp.name}</span>
            </div>
            <span className="text-slate-500 text-xs">#{comp.assembly_order || i + 1}</span>
          </motion.div>
        ))}
      </div>

      {explodedData.assembly_sequence && (
        <div className="space-y-2">
          <span className="text-slate-400 text-xs font-medium">
            {language === 'tr' ? 'Montaj Sırası' : 'Assembly Sequence'}
          </span>
          <div className="flex flex-wrap gap-1">
            {explodedData.assembly_sequence.slice(0, 6).map((step, i) => (
              <span key={i} className="px-2 py-1 bg-purple-500/20 text-purple-300 rounded text-xs">
                {step.step}. {step.component}
              </span>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
